"use strict";

import ajaxHelper from '../../helpers/ajax-helper.js';
import urlHelper from '../../helpers/url-helper.js';
import dataLayerModule from '../../Shared/data-layer-module.js';
import informizelyFeedbackModule from '../../Shared/informizely-module.js';
import upsellModalFilterModule from './upsell-modal-filter-module.js';
import tippy from 'tippy.js';

const packageUpsellModule = (function () {
    let isLoading = false;
    let selectedPackageUrl = null;
    let selectedPackageData = null;

    const getLoadingHtml = () => {
        return `<div class="m-small-flex--header">
                    <h2 role="status" aria-live="polite">Packages options</h2>
                    <div class="gaudi-modal__close" title="Close" data-dismiss="modal">
                        <img loading="lazy" src="/img/gaudi-modals/close.svg">
                    </div>
                </div>

                <div class="m-small-flex--content custom-scrollbar">
                    <div class="skeleton-loader" role="status" aria-live="polite">
                        <p>Loading in progress</p>
                            <div class="skeleton-loader__item"></div>
                            <div class="skeleton-loader__item"></div>
                            <div class="skeleton-loader__item"></div>
                    </div>
                </div>

                <div class="m-small-flex--footer">
                    <div class="btn-group btn-group--center">
                        <button class="gaudi-btn gaudi-btn--ghost" disabled data-dismiss="modal">Cancel</button>
                        <button class="gaudi-btn gaudi-btn--primary" disabled>Select Package</button>
                    </div>
                </div>`;
    };

    const initTippy = () => {
        tippy("[data-tippy-content]", {
            allowHTML: true,
            appendTo: document.body,
            zIndex: 99999,
            theme: 'light',
            placement: 'top',
            hideOnClick: false,
            trigger: 'mouseenter focus'
        });
    }

    const initPreselectedPackage = () => {
        const preselectedPackage = document.getElementById('preselected-package');
        if (preselectedPackage) {
            const packageUrl = preselectedPackage.dataset.packageUrl;
            const selectButton = document.getElementById('select-package-btn');

            if (selectButton && packageUrl) {
                selectButton.removeAttribute('disabled');
                selectedPackageUrl = packageUrl;
                selectedPackageData = {
                    cruiseId: preselectedPackage.dataset.cruiseId,
                    packageId: preselectedPackage.dataset.packageId
                };
            }
        }
    }

    const getOccupancyString = () => {
        if (typeof priceSectionModule !== 'undefined') {
            const selectedOccupancy = priceSectionModule.getSelectedCabinOccupationType();
            return priceSectionModule.mapOccupancyIdToString(selectedOccupancy?.id);
        }

        // Not sure a better way to get this on the search page.
        const occupancyRefinements = window.getOccupancyRefinements?.() || [];
        return occupancyRefinements[0]?.toLowerCase().replace(/\s+/g, '-') || 'double';
    };

    const openModal = (modal) => {
        modal.setAttribute('aria-modal', 'true');
        document.body.classList.add('noscroll');
    };

    const closeModal = (modal) => {
        modal.setAttribute('aria-modal', 'false');
        document.body.classList.remove('noscroll');
    };

    const togglePackageUpsell = async (cruiseId = null, airportCode = null, packageRotationId = null, originalCruiseId = null) => {
        if (isLoading) return;
        const modal = document.getElementById('packages-options');
        const content = document.getElementById('packages-options-content');
        if (!modal || !content) return;

        const occupancyString = getOccupancyString();
        const url = urlHelper.getPackageUpsellUrl(cruiseId, airportCode, packageRotationId, occupancyString, originalCruiseId);
        if (!url) return;

        // Open the modal immediately with a skeleton loader so the user gets instant feedback,
        // then fetch the actual content. Without this the modal wrapper stays display:none
        // (aria-modal="false") until the request completes, making the whole thing feel slow.
        isLoading = true;
        content.innerHTML = getLoadingHtml();
        openModal(modal);

        try {
            const html = await ajaxHelper.getAsync(url, true);

            // Defensive: the modal partial renders an empty body when Packages is empty
            // (@if (Model.Packages.Any())). The trigger is meant to be hidden in that case,
            // but if we ever get here with nothing to show, close the modal rather than
            // presenting an empty shell.
            if (!html || !html.trim()) {
                console.error('No alternative packages returned for package upsell:', { cruiseId, originalCruiseId });
                closeModal(modal);
                return;
            }

            content.innerHTML = html;
            informizelyFeedbackModule.updateFeedbackButtonVisibility();
            initTippy();
            initPreselectedPackage();
            upsellModalFilterModule.init(content);

            const packageOptions = document.querySelectorAll('li[data-package-url]');
            dataLayerModule.addViewItemListToDataLayerForPackageUpsell(packageOptions, originalCruiseId ?? cruiseId);
        } catch (error) {
            console.error('Error loading packages:', error);
            closeModal(modal);
        } finally {
            isLoading = false;
        }
    };

    const handleDocumentClick = (event) => {
        if (informizelyFeedbackModule.handleFeedbackButtonClick(event)) {
            return;
        }

        const closeModalButton = event.target.closest('[data-modal-close]');
        const packageUpsellButton = event.target.closest('#packageUpsellBtn');
        const packageOverviewButton = event.target.closest('.package-overview__btn');
        const packageOption = event.target.closest('li[data-package-url]');

        if (closeModalButton) {
            const modal = closeModalButton.closest('.gaudi-modal__wrapper');
            if (modal) {
                modal.setAttribute('aria-modal', 'false');
                document.body.classList.remove('noscroll');
            }
        }

        if (packageUpsellButton) {
            const cruiseId = packageUpsellButton.dataset.cruiseId;
            const originalCruiseId = packageUpsellButton.dataset.originalCruiseId;
            const airportCode = packageUpsellButton.dataset.airportCode;
            const packageId = packageUpsellButton.dataset.packageId;

            togglePackageUpsell(cruiseId, airportCode, packageId, originalCruiseId);
        }

        const viewAllPackagesButton = event.target.closest('[data-action="view-all-hotel-packages"]');
        if (viewAllPackagesButton) {
            event.preventDefault();
            const cruiseId = viewAllPackagesButton.dataset.cruiseId;
            const originalCruiseId = viewAllPackagesButton.dataset.originalCruiseId;
            const airportCode = viewAllPackagesButton.dataset.airportCode;
            const packageId = viewAllPackagesButton.dataset.packageId;

            togglePackageUpsell(cruiseId, airportCode, packageId, originalCruiseId);
        }

        if (packageOverviewButton && !packageOverviewButton.classList.contains('package-overview__view-more')) {
            const cruiseId = packageOverviewButton.dataset.cruiseId;
            const originalCruiseId = packageOverviewButton.dataset.originalCruiseId;
            const airportCode = packageOverviewButton.dataset.airportCode;
            const packageId = packageOverviewButton.dataset.packageId;

            if (cruiseId && packageId) {
                togglePackageUpsell(cruiseId, airportCode, packageId, originalCruiseId);
            }
        }

        if (event.target.matches('.gaudi-btn.gaudi-btn--primary') && selectedPackageUrl) {
            if (selectedPackageData) {
                dataLayerModule.addSelectItemToDataLayerForPackageUpsell(selectedPackageData.cruiseId, selectedPackageData.packageId);
            }

            const openNewTabFlag = document.querySelector('[data-open-new-tab="true"]');
            if (openNewTabFlag) {
                window.open(selectedPackageUrl, '_blank', 'noopener');
            } else {
                window.location.href = selectedPackageUrl;
            }
        }

        if (packageOption) {
            const packageUrl = packageOption.dataset.packageUrl;
            const packageList = packageOption.closest('ul');
            packageList.querySelectorAll('li').forEach(li => li.setAttribute('aria-selected', 'false'));
            packageOption.setAttribute('aria-selected', 'true');

            const selectButton = document.getElementById('select-package-btn');
            if (selectButton && packageUrl) {
                selectButton.removeAttribute('disabled');
                selectedPackageUrl = packageUrl;
                selectedPackageData = {
                    cruiseId: packageOption.dataset.cruiseId,
                    packageId: packageOption.dataset.packageId
                };
            }
        }

        const packageCard = event.target.closest('[data-package-url]');
        if (packageCard && packageCard.closest('#packageList') && !event.target.closest('button')) {
            const upsellBtn = document.getElementById('packageUpsellBtn');
            togglePackageUpsell(
                packageCard.dataset.cruiseId,
                upsellBtn?.dataset.airportCode,
                packageCard.dataset.packageId,
                upsellBtn?.dataset.originalCruiseId
            );
        }

        const expandButton = event.target.closest('.package-card__expand-btn');
        if (expandButton) {
            const sublist = expandButton.closest('.package-card__sublist');
            const isExpanded = sublist.dataset.expanded === 'true';
            const allItems = sublist.querySelectorAll('span');
            const viewMoreLabel = expandButton.dataset.viewMore;
            const viewLessLabel = expandButton.dataset.viewLess;
            const moreLabel = expandButton.dataset.more;

            if (isExpanded) {
                allItems.forEach((item, index) => {
                    if (index >= 3) item.classList.add('package-card__sublist-hidden');
                });
                const hiddenCount = allItems.length - 3;
                expandButton.textContent = `${viewMoreLabel} ${hiddenCount} ${moreLabel}`;
                sublist.dataset.expanded = 'false';
            } else {
                allItems.forEach(item => item.classList.remove('package-card__sublist-hidden'));
                expandButton.textContent = viewLessLabel;
                sublist.dataset.expanded = 'true';
            }
        }
    };

    document.addEventListener('click', handleDocumentClick);
    informizelyFeedbackModule.init();

    // Public API
    return {
        togglePackageUpsell
    };
})();

export default packageUpsellModule;